/** @jsxImportSource @emotion/react */
import {
  Container,
  Icon,
  Logo,
  Stack,
  Text,
  useBreakpoint,
  useTheme,
  VisuallyHidden,
} from "basis";
import { getRegional } from "library/getRegional";
import { useAuthentication } from "library/hooks/auth/useAuthentication";
import { useConfigFeature } from "library/hooks/useConfig";
import { useEffect, useRef, useState } from "react";
import { useEventTracking } from "react-event-tracker";
import { Link, useLocation } from "react-router-dom";
import { BpayIcon } from "./temp-icons/BpayIcon";
import { DirectDebitIcon } from "./temp-icons/DirectDebitIcon";

export const SIDEBAR_WIDTH = 240; // px

const listStyle = {
  margin: 0,
  padding: 0,
  listStyleType: "none",
};

const TEMP_ICONS = {
  "direct-debit": DirectDebitIcon,
  bpay: BpayIcon,
};

type NavItemIconProps = {
  name: string;
};

const NavItemIcon = ({ name }: NavItemIconProps) => {
  const TempIcon = TEMP_ICONS[name];

  return TempIcon ? (
    <TempIcon />
  ) : (
    <Icon name={name} color="white" size="24px" />
  );
};

type NavItemProps = {
  href?: string;
  onClick?: () => void;
  icon: string;
  children: string;
};

const NavItem = ({ href, onClick, icon, children }: NavItemProps) => {
  const theme = useTheme();
  const { trackEvent } = useEventTracking();
  const location = useLocation();
  const { pathname } = location;
  const isActive =
    href &&
    pathname.startsWith(href) &&
    [undefined, "/"].includes(pathname[href.length]);
  const css = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    padding: `${theme.space[2]} ${theme.space[4]}`,
    ...(isActive
      ? {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
        }
      : {
          backgroundColor: "transparent",
          opacity: 0.7,
          transition: "background-color 0.1s",
          transitionTimingFunction: "ease-in",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            transitionTimingFunction: "ease-out",
          },
        }),
  };
  const handleOnClick = () => {
    trackEvent({
      event: {
        category: "text-link",
        action: "internal-link",
        location: "menu-nav",
        label: children,
      },
    });
  };

  if (href) {
    return (
      <Link to={href} css={{ textDecoration: "none" }} onClick={handleOnClick}>
        <span css={css}>
          <NavItemIcon name={icon} />
          <Text margin="0 0 0 2" color="white">
            {children}
          </Text>
        </span>
      </Link>
    );
  }

  return (
    <button
      type="button"
      css={css}
      onClick={() => {
        handleOnClick();
        onClick && onClick();
      }}
    >
      <NavItemIcon name={icon} />
      <Text margin="0 0 0 2" color="white">
        {children}
      </Text>
    </button>
  );
};

const DESKTOP_BREAKPOINTS = ["sm", "md", "lg", "xl"];

export const Sidebar = () => {
  const theme = useTheme();
  const location = useLocation();
  const { pathname } = location;
  const prevPathname = useRef<string>();
  const isNZ = getRegional("code") === "NZ";
  const showBpay = useConfigFeature("bpay") && !isNZ;
  const showDirectDebit = useConfigFeature("directDebit") && !isNZ;
  const showManagePersonalDetails = useConfigFeature("managePersonalDetails");
  const showNotifications = useConfigFeature("notifications");
  const breakpoint = useBreakpoint();
  const isDesktop = DESKTOP_BREAKPOINTS.includes(breakpoint);
  const isPrevBreakpointDesktop = useRef<boolean>();
  const [showNav, setShowNav] = useState(isDesktop);
  const { logoutUser } = useAuthentication();

  useEffect(() => {
    if (
      // mobile => desktop
      isPrevBreakpointDesktop.current === false &&
      isDesktop === true
    ) {
      setShowNav(true);
    } else if (
      // desktop => mobile
      isPrevBreakpointDesktop.current === true &&
      isDesktop === false
    ) {
      setShowNav(false);
    }

    isPrevBreakpointDesktop.current = isDesktop;
  }, [isDesktop]);

  useEffect(() => {
    document.body.style.overflowY =
      !isDesktop && showNav ? "hidden" : "initial";
  }, [isDesktop, showNav]);

  useEffect(() => {
    if (pathname !== prevPathname.current && !isDesktop) {
      setShowNav(false);
    }

    prevPathname.current = pathname;
  }, [isDesktop, pathname]);

  return (
    <div
      css={{
        flexShrink: 0,
        height: showNav ? "100vh" : "auto",
        position: "sticky",
        top: 0,
        zIndex: 1,
        [theme.minMediaQueries.sm]: {
          position: "initial",
          width: SIDEBAR_WIDTH,
        },
      }}
    >
      <div
        css={{
          height: "100%",
          background:
            "linear-gradient(135deg, hsl(215,100%,33%) 0%, hsl(215,100%,20%) 100%);",
        }}
      >
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: `${theme.space[3]} ${theme.space[3]} ${theme.space[3]} ${theme.space[4]}`,
              [theme.minMediaQueries.sm]: {
                padding: theme.space[4],
              },
            }}
          >
            <Logo
              color="white"
              height="36"
              height-xs="40"
              name={isNZ ? "gem" : "latitude"}
            />
            <Container hide-sm>
              <button
                type="button"
                css={{
                  width: 48,
                  height: 48,
                  padding: 0,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 4,
                  backgroundColor: "transparent",
                  transition: "background-color 0.1s",
                  transitionTimingFunction: "ease-in",
                  outline: 0,
                  ...theme.focusStyles.focusVisible,
                  ":hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    transitionTimingFunction: "ease-out",
                  },
                }}
                onClick={() => {
                  setShowNav((show) => !show);
                }}
              >
                <Icon name={showNav ? "cross" : "hamburger"} color="white" />
                <VisuallyHidden>
                  {showNav ? "Hide menu" : "Show menu"}
                </VisuallyHidden>
              </button>
            </Container>
          </div>
          {showNav && (
            <nav
              css={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
            >
              <Stack gap="10" margin="8 0 10 0">
                <NavItem href="/" icon="home">
                  Overview
                </NavItem>
                <ul css={listStyle}>
                  <li>
                    <Text textStyle="overline" color="white" margin="0 0 2 5">
                      <strong>Payments</strong>
                    </Text>
                    <ul css={listStyle}>
                      <li>
                        <NavItem href="/ways-to-pay" icon="payment">
                          Make a payment
                        </NavItem>
                      </li>
                      {showBpay && (
                        <li>
                          <NavItem href="/bpay" icon="bpay">
                            BPAY
                          </NavItem>
                        </li>
                      )}
                      {showDirectDebit && (
                        <li>
                          <NavItem href="/direct-debit" icon="direct-debit">
                            Direct debit
                          </NavItem>
                        </li>
                      )}
                    </ul>
                  </li>
                </ul>
                <ul css={listStyle}>
                  <li>
                    <Text textStyle="overline" color="white" margin="0 0 2 5">
                      <strong>Manage</strong>
                    </Text>
                    <ul css={listStyle}>
                      <li>
                        <NavItem href="/link-account" icon="link">
                          Link account
                        </NavItem>
                      </li>
                      <li>
                        <NavItem href="/manage-account" icon="document">
                          Manage account
                        </NavItem>
                      </li>
                    </ul>
                  </li>
                </ul>
                <ul css={listStyle}>
                  <li>
                    <Text textStyle="overline" color="white" margin="0 0 2 5">
                      <strong>Support</strong>
                    </Text>
                    <ul css={listStyle}>
                      <li>
                        <NavItem href="/faq" icon="question">
                          FAQ
                        </NavItem>
                      </li>
                      <li>
                        <NavItem href="/contact" icon="call">
                          Contact us
                        </NavItem>
                      </li>
                    </ul>
                  </li>
                </ul>
                {(showManagePersonalDetails || showNotifications) && (
                  <ul css={listStyle}>
                    <li>
                      <Text textStyle="overline" color="white" margin="0 0 2 5">
                        <strong>Profile</strong>
                      </Text>
                      <ul css={listStyle}>
                        {showManagePersonalDetails && (
                          <li>
                            <NavItem
                              href="/manage-personal-details"
                              icon="person"
                            >
                              Manage personal details
                            </NavItem>
                          </li>
                        )}
                        {showNotifications && (
                          <li>
                            <NavItem href="/notifications" icon="mail">
                              Notifications
                            </NavItem>
                          </li>
                        )}
                      </ul>
                    </li>
                  </ul>
                )}
              </Stack>
              <div css={{ margin: `auto 0 ${theme.space[6]}` }}>
                <NavItem
                  onClick={() => {
                    logoutUser();
                  }}
                  icon="logout"
                >
                  Logout
                </NavItem>
              </div>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
};
